/**
 * Nosto cart-drawer recommendations ("You may also like").
 *
 * Fills <div class="nosto_element" id="cartpage-nosto-1"> in sections/cart-drawer.liquid.
 *
 * WHY THIS FILE EXISTS — it replaces <nosto-campaign cart-synced>
 * ---------------------------------------------------------------
 * The placement used to be `<nosto-campaign id="cartpage-nosto-1" cart-synced>` from
 * @nosto/web-components. That component loads every campaign like this:
 *
 *     createRecommendationRequest({ includeTagging: true })
 *
 * `includeTagging: true` means "initialise the request from the page tagging". On a PDP the page
 * tagging carries the product view, so the request went out with `events: [["vp", <productId>]]`
 * and Nosto recorded ANOTHER view of the product the shopper was already looking at. The component
 * reloads on mount, on Nosto's `cartupdated`, and again every time CartDrawer#replaceContent tore
 * it out and rebuilt it — so one PDP view plus one add to cart logged six product views instead of
 * one. `includeTagging: true` is hardcoded in the component; no attribute turned it off.
 *
 * So we ask for the same campaign ourselves, WITHOUT tagging, passing the cart explicitly via
 * setCartContent(). Verified against the live store: identical campaign HTML, and the resulting
 * /ev1 request carries no `events` key at all. Full measurements and the fix rationale live in
 * NOSTO-DUPLICATE-VP-PLAN.md in the orlakiely-nosto repo.
 *
 * DO NOT "simplify" this back to <nosto-campaign>. It will silently start double-counting product
 * views again, and nothing on the storefront will look wrong.
 *
 * WHAT DRIVES A RELOAD
 * --------------------
 *   - first load       — the drawer only renders the placement when the cart is non-empty
 *                        (sections/cart-drawer.liquid gates on `cart.empty?`), so on most page
 *                        loads there is nothing to fill and this does nothing at all;
 *   - cart:change      — native adds, quantity changes, removals. The event carries the whole cart
 *                        in `detail.cart`, so no extra /cart.js read is needed;
 *   - cart:refresh     — wishlist, the Nosto carousel, any programmatic mini-cart refresh. No
 *                        detail, so the cart is read from /cart.js.
 *
 * Both events are the ones _nosto-cart-sync.js already listens on, and both are dispatched at
 * document, so one document-level listener catches every surface.
 *
 * THE DRAWER WIPES THIS DIV
 * -------------------------
 * CartDrawer#replaceContent (assets/theme.js) does `replaceChildren(...)` on every cart:change, so
 * the div we just filled is thrown away and a fresh empty one takes its place. The drawer then
 * dispatches `cart-drawer:refreshed`, which we use to paint the last campaign back immediately
 * from cache — no network — so the drawer never opens on an empty gap. The debounced reload that
 * the same cart:change kicked off then replaces it with fresh recommendations a moment later.
 */

import { recommendationsEnabled } from './_nosto-flags.js';
import { toNostoItem } from './_nosto-cart-sync.js';

const PLACEMENT = 'cartpage-nosto-1';
const RELOAD_DEBOUNCE_MS = 200;

// Same debug ergonomics as the other two Nosto modules, so all three are driven the same way.
// Enable with `nostoCartRecs.on()` (persists across navigations); `.off()` to stop.
const DEBUG_KEY = 'nostoCartRecsDebug';

function debugEnabled() {
  if (window.nostoCartRecsDebug) return true;
  try {
    return window.localStorage.getItem(DEBUG_KEY) === 'true';
  } catch (e) {
    return false; // storage can throw in private mode / blocked-cookie contexts
  }
}

function setDebug(on) {
  try {
    if (on) window.localStorage.setItem(DEBUG_KEY, 'true');
    else window.localStorage.removeItem(DEBUG_KEY);
  } catch (e) {
    window.nostoCartRecsDebug = on;
  }
  console.log('[nosto-cart-recs] debug ' + (on ? 'ON — change the cart' : 'off'));
}

function debug(msg, data) {
  if (!debugEnabled()) return;
  if (data === undefined) console.log('[nosto-cart-recs] ' + msg);
  else console.log('[nosto-cart-recs] ' + msg, data);
}

/** The last campaign Nosto returned, kept so a drawer re-render can repaint without a request. */
let lastCampaign = null;

function target() {
  return document.getElementById(PLACEMENT);
}

/**
 * Hand a campaign to Nosto to inject. Nosto's own injection (rather than setting innerHTML) is
 * what wires up its click tracking on the rendered products, so the rows stay attributable.
 */
function inject(api, campaign, el) {
  return api.placements
    .injectCampaigns({ [PLACEMENT]: campaign }, { [PLACEMENT]: el })
    .then((result) => {
      debug('injected', result);
      return result;
    });
}

// Unlike the sync in _nosto-cart-sync.js, a dropped reload here is visible: it leaves the shopper
// looking at recommendations for a cart they no longer have. So a change that lands mid-flight
// isn't discarded — it's remembered and re-run once the in-flight load finishes.
let inFlight = false;
let staleCart = null;
function loadRecommendations(cart) {
  const el = target();
  if (!el) {
    debug('skipping — no #' + PLACEMENT + ' on the page (empty cart?)');
    return;
  }
  // Nosto's script is injected by the Shopify app with no theme-side stub, so a consent tool
  // (Pandectes) or an ad blocker can keep it out entirely. Bail rather than throw.
  if (typeof window.nostojs !== 'function') {
    debug('skipping — window.nostojs is absent (blocked by consent tool or ad blocker?)');
    return;
  }
  if (inFlight) {
    debug('a load is already in flight — queued to re-run with this cart');
    staleCart = cart;
    return;
  }
  inFlight = true;

  const items = (cart.items || []).map((item) => toNostoItem(item, cart.currency));
  debug('requesting ' + PLACEMENT + ' with ' + items.length + ' cart line(s)', items);

  window.nostojs((api) => {
    api
      .createRecommendationRequest() // NOTE: no { includeTagging: true } — see the header
      .disableCampaignInjection() // we inject by hand, into the div we own
      .setElements([PLACEMENT])
      .setResponseMode('HTML')
      .setCartContent({ items })
      .load()
      .then(({ recommendations }) => {
        const campaign = recommendations && recommendations[PLACEMENT];
        if (!campaign) {
          debug('Nosto returned no campaign for ' + PLACEMENT);
          return;
        }
        lastCampaign = campaign;
        // Re-read the target: the drawer may have replaced it while the request was in flight.
        const current = target();
        if (!current) {
          debug('campaign arrived but the placement has gone — cached for the next repaint');
          return;
        }
        return inject(api, campaign, current);
      })
      .catch((e) => debug('load failed', e))
      .finally(() => {
        inFlight = false;
        if (staleCart) {
          const next = staleCart;
          staleCart = null;
          debug('re-running the load that arrived mid-flight');
          loadRecommendations(next);
        }
      });
  });
}

/**
 * Read the cart for a reload. `cart:change` already carries the whole cart, so use it rather than
 * asking Shopify for what we were just handed; `cart:refresh` carries nothing, so fetch.
 */
function cartFor(event) {
  const fromEvent = event && event.detail && event.detail.cart;
  if (fromEvent && Array.isArray(fromEvent.items)) {
    debug('cart taken from the event payload');
    return Promise.resolve(fromEvent);
  }
  debug('reading /cart.js');
  return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((res) => res.json());
}

let debounceTimer = null;
function scheduleReload(reason, event) {
  debug('cart event: ' + reason + ' → reload scheduled');
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    cartFor(event)
      .then(loadRecommendations)
      .catch((e) => debug('reload failed — could not read the cart', e));
  }, RELOAD_DEBOUNCE_MS);
}

/** Repaint the cached campaign into the fresh div the drawer just built. No network. */
function repaintFromCache() {
  if (!lastCampaign) return;
  const el = target();
  if (!el || el.childElementCount > 0) return;
  debug('drawer re-rendered → repainting the cached campaign');
  window.nostojs((api) => inject(api, lastCampaign, el));
}

let bound = false;
function init() {
  if (bound) return;
  bound = true;

  document.addEventListener('cart:change', (e) => scheduleReload('cart:change', e));
  document.addEventListener('cart:refresh', (e) => scheduleReload('cart:refresh', e));
  document.addEventListener('cart-drawer:refreshed', repaintFromCache);

  // First fill. Only does anything when the cart is already non-empty, which is the only case
  // where the drawer renders the placement at all.
  if (target()) scheduleReload('initial load', null);
}

// Off unless the "Nosto recommendations" theme setting is on — with it off the placement isn't
// rendered and there is nothing to fill. See _nosto-flags.js.
if (recommendationsEnabled()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
